// In src/hooks/useNavigation.js
import { useMemo } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import { useAuthUser } from '../contexts/AuthUserContext';
import { allPages, customerConfig, pageCategories } from '../utils/navigationConfig';

export const useNavigation = (view) => {
  const { currentCustomer } = useCustomer();
  const user = useAuthUser();

  const navigation = useMemo(() => {

    const emptyNav = { routes: [], navLinks: [] };

    if (view === 'admin' || (user?.isHeliosAdmin && view !== 'client')) {
        const adminPages = Object.values(allPages).filter(page => page.category === pageCategories.ADMIN);
        
        const adminNavLinks = adminPages.map(page => ({
            path: page.path, // Admin paths are absolute
            title: page.title,
            icon: page.icon,
        }));
        // Admin routes are handled statically in AppRoutes for now, so we don't return them here.
        return { routes: [], navLinks: adminNavLinks };
    }

    if (!currentCustomer) return emptyNav;

    const customerId = currentCustomer.id;
    
    // Debug logging to help diagnose the issue
    // console.log('useNavigation - currentCustomer:', currentCustomer);
    // console.log('useNavigation - customerId:', customerId);
    // console.log('useNavigation - customerConfig for this ID:', customerConfig[customerId]);
    
    const config = customerConfig[customerId] || customerConfig.default;
    
    // Always use the category from the config, ignore the one from currentCustomer
    // This ensures that the navigationConfig.js file is the single source of truth for categories
    const customerCategory = config.category;
    
    // console.log('useNavigation - Using category:', customerCategory);

    const clientPages = Object.values(allPages).filter(page => {
        if(page.category === pageCategories.ADMIN) return false;

        // Check for exclusion first
        if (config.exclude?.includes(page.path)) return false;
        
        // Check for explicit inclusion
        if (config.include?.includes(page.path)) return true;
        
        // Check for category match or if it's a common page
        return page.category === customerCategory || page.category === pageCategories.COMMON;
    }).sort((a, b) => {
      const getPriority = (page) => {
        const pageCategory = page.category;

        // Priority 1: "Client" specific pages.
        // These are pages not in the customer's main category or common, added via 'include'.
        if (pageCategory !== customerCategory && pageCategory !== pageCategories.COMMON) {
          return 1;
        }

        // Priority 2: "Category" pages.
        if (pageCategory === customerCategory) {
          return 2;
        }

        // Priority 3: "General" (Common) pages.
        if (pageCategory === pageCategories.COMMON) {
          return 3;
        }

        return 4; // Fallback.
      };
      return getPriority(a) - getPriority(b);
    });

    // console.log('clientPages', clientPages);

    // Format for router and sidebar
    const routes = clientPages.map(page => ({
        path: page.path,
        element: page.element // You'd need to add the element to the config, e.g., element: <CallHistory />
    }));

    const navLinks = clientPages.map(page => ({
        path: `/app/${customerId}/${page.path}`, // Note the new dynamic URL structure
        title: page.title,
        icon: page.icon
    }));

    return { routes, navLinks };
  }, [currentCustomer, user, view]);

  return navigation;
};